import {
  Button,
  ButtonList,
  DropdownButton,
  Page,
  SearchInput,
  SimplePagination,
  Table,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { NotificationPayloadT, Team } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMount, useSetState } from 'react-use';

import { TableLoadingRow } from 'components/shared/tableLoadingRow';
import { WithTeamsInjectedProps } from 'hocs/teams/withTeams';

import { ConfirmModal } from '../../../shared/confirmModal/components/confirmModal';
import { TeamsCreateModal } from '../containers/teamsCreateModal';

import styles from './teams.module.scss';
import { TeamsTableRow } from './teamsTableRow';

export type TeamsDispatchProps = {
  notificationAdd: (payload: NotificationPayloadT) => void;
};

export type TeamsStateProps = {};

type TeamsProps = WithTeamsInjectedProps & TeamsDispatchProps & TeamsStateProps;

type TeamsState = {
  isConfirmModalOpen: boolean;
  isCreateModalOpen: boolean;
  currentTeam?: Team;
  waitingForFetch: boolean;
};

const FETCH_DELAY = 1000;

export const Teams = (props: TeamsProps): JSX.Element => {
  const navigate = useNavigate();

  const hasManageTeamsAccess = useHasAccess('organization.team');

  const [state, setState] = useSetState<TeamsState>({
    isConfirmModalOpen: false,
    isCreateModalOpen: false,
    waitingForFetch: false,
  });

  useMount(() => {
    props.teams.getItems();
  });

  const setWaitingForFetch = (waitingForFetch: boolean) => {
    setState({
      waitingForFetch,
    });
  };

  const delayedFetchItems = () => {
    const { teams } = props;
    const { getItems } = teams;
    setTimeout(() => {
      getItems().then(() => {
        setWaitingForFetch(false);
      });
    }, FETCH_DELAY);
  };

  const handleSearchSubmit = (searchTerm: string) => {
    props.teams.searchTermChange(searchTerm, 0);
  };

  const handleSearchToggle = (isOpen: boolean) => {
    if (!isOpen && props.teams.searchTerm) {
      props.teams.searchTermChange('', 0);
    }
  };

  const handleSortByNameAsc = () => {
    props.teams.sortByChange('name', 'asc');
  };

  const handleSortByNameDesc = () => {
    props.teams.sortByChange('name', 'desc');
  };

  const gotoNextPage = () => {
    props.teams.nextPage();
  };

  const gotoPreviousPage = () => {
    props.teams.previousPage();
  };

  const showRemoveModal = (team: Team) => {
    setState({ isConfirmModalOpen: true, currentTeam: team });
  };

  const hideRemoveModal = () => {
    setState({ isConfirmModalOpen: false });
  };

  const handleDeleteTeam = () => {
    ampli.settingsTeamRemoveTeamConfirmed();

    const { teams, notificationAdd } = props;
    const { remove } = teams;
    const { currentTeam } = state;

    if (currentTeam) {
      setWaitingForFetch(true);
      remove(currentTeam.id)
        .then((r) => {
          setState({ isConfirmModalOpen: false });
          const notificationOptions = {
            title: 'Team deleted',
            text: `"${currentTeam.name}" team has been deleted`,
            icon: 'i-done',
            className: 'success',
            time: 5000,
          };

          notificationAdd(notificationOptions);
          delayedFetchItems();
        })
        .catch((e) => {
          const notificationOptions = {
            title: 'An error occurred',
            text: `An error occurred when trying to delete the "${currentTeam.name}" team`,
            icon: 'i-exclamation',
            className: 'danger',
          };

          notificationAdd(notificationOptions);
          setWaitingForFetch(false);
          console.error('Delete error', e);
        });
    }
  };

  const showCreateModal = () => {
    ampli.settingsTeamAddTeamButtonClicked();
    setState({ isCreateModalOpen: true });
  };

  const handleCreateModalClose = (refetchData = true) => {
    setState({ isCreateModalOpen: false });

    if (refetchData) {
      setWaitingForFetch(true);
      delayedFetchItems();
    }
  };

  const handleRetry = () => {
    props.teams.getItems();
  };

  const goToDetailsPage = (teamId: string) => {
    navigate(`/settings/teams/${teamId}`);
  };

  const renderLoadingStatus = () => {
    const { teams } = props;

    if (teams.getItemsAsyncStatus === 'failed') {
      return (
        <Table.Tr>
          <Table.Td>
            Something went wrong...
            <Button variant="tertiary" onClick={handleRetry}>
              Retry
            </Button>
          </Table.Td>
        </Table.Tr>
      );
    }

    return <TableLoadingRow colSpan={3} />;
  };

  const renderSuccessStatus = () => {
    const { teams } = props;
    const { items } = teams;

    let success: React.ReactNode;

    if (items.length > 0) {
      success = items.map((team, i) => (
        <TeamsTableRow
          key={i}
          team={team}
          showRemoveModal={showRemoveModal}
          goToDetailsPage={goToDetailsPage}
        />
      ));
    } else {
      success = (
        <Table.Tr>
          <Table.Td>No teams found.</Table.Td>
        </Table.Tr>
      );
    }

    return success;
  };

  const renderFoot = () => {
    const { teams } = props;
    const { hasNextPage, hasPreviousPage, getItemsAsyncStatus } = teams;

    const foot = (
      <Table.Tfoot>
        <Table.Tr>
          <Table.Td colSpan={5}>
            <SimplePagination
              previousButtonClick={gotoPreviousPage}
              previousButtonDisabled={!hasPreviousPage}
              nextButtonClick={gotoNextPage}
              nextButtonDisabled={!hasNextPage}
              loading={getItemsAsyncStatus === 'loading'}
            />
          </Table.Td>
        </Table.Tr>
      </Table.Tfoot>
    );

    return foot;
  };

  const { teams } = props;
  const { hasNextPage, hasPreviousPage, getItemsAsyncStatus, searchTerm } =
    teams;
  const { waitingForFetch } = state;

  const showFoot = hasNextPage || hasPreviousPage;

  return (
    <>
      <Page.Section
        title="All teams"
        controls={
          <>
            <SearchInput
              onSubmit={handleSearchSubmit}
              onToggle={handleSearchToggle}
            />

            <DropdownButton
              buttonProps={{ disabled: !!searchTerm.length }}
              dropdown={
                <ButtonList>
                  <ButtonList.Item
                    icon="arrow-up"
                    onClick={handleSortByNameAsc}
                  >
                    A - Z
                  </ButtonList.Item>
                  <ButtonList.Item
                    icon="arrow-down"
                    onClick={handleSortByNameDesc}
                  >
                    Z - A
                  </ButtonList.Item>
                </ButtonList>
              }
            >
              Sort by
            </DropdownButton>

            {hasManageTeamsAccess && (
              <Button onClick={showCreateModal}>Add team</Button>
            )}
          </>
        }
      >
        <Table framed stickyHeader stickyFooter headerWithBackground>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Team</Table.Th>
              <Table.Th className={styles.headCellMembers}>Members</Table.Th>
              <Table.Th className={styles.headCellActions} />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {!waitingForFetch && getItemsAsyncStatus === 'succeeded'
              ? renderSuccessStatus()
              : renderLoadingStatus()}
          </Table.Tbody>

          {showFoot && renderFoot()}
        </Table>
      </Page.Section>

      <ConfirmModal
        isShown={state.isConfirmModalOpen}
        headline="Are you sure?"
        text={`Are you sure you want to remove the "${state.currentTeam?.name}" team?`}
        confirmText="Yes, delete it now"
        cancelText="Cancel"
        confirm={handleDeleteTeam}
        cancel={hideRemoveModal}
      />

      <TeamsCreateModal
        isOpen={state.isCreateModalOpen}
        onClose={handleCreateModalClose}
        createTeam={teams.create}
      />
    </>
  );
};
